import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Field, fieldInputCls } from "@/components/Field";
import { ScanButton } from "@/components/BarcodeScanner";
import { useT } from "@/lib/i18n";
import { useSettings } from "@/context/SettingsContext";
import { api, formatApiError } from "@/lib/api";
import { TYRES } from "@/lib/format";

const blankTyre = () => ({ barcode: "", circumference: "", weight: "", hardness: "" });
const blankSet = () => ({ name: "", brand: "", model: "", notes: "", tyres: Object.fromEntries(TYRES.map((p) => [p, blankTyre()])) });
const num = (v) => (v === "" || v == null ? null : Number(v));

const TyreRow = ({ pos, value, onChange }) => {
  const { t } = useT();
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div data-testid={`tyreset-row-${pos}`} className="grid grid-cols-3 sm:grid-cols-[70px_1.8fr_1fr_1fr_1fr] gap-2 items-end border-t border-[#282D3D] pt-3">
      <div className="col-span-3 sm:col-span-1 font-heading uppercase font-bold text-base sm:pb-2">{t(`tyre_${pos}`)}</div>
      <Field label={t("barcode")} className="col-span-3 sm:col-span-1">
        <div className="flex gap-1.5">
          <Input data-testid={`tyreset-${pos}-barcode-input`} value={value.barcode ?? ""} onChange={set("barcode")} className={fieldInputCls} />
          <ScanButton testid={`tyreset-${pos}-scan-btn`} onScan={(code) => onChange({ ...value, barcode: code })} />
        </div>
      </Field>
      <Field label={t("circumference")}>
        <Input data-testid={`tyreset-${pos}-circumference-input`} type="number" step="0.1" inputMode="decimal" value={value.circumference ?? ""} onChange={set("circumference")} className={fieldInputCls} />
      </Field>
      <Field label={t("weight")}>
        <Input data-testid={`tyreset-${pos}-weight-input`} type="number" step="0.01" inputMode="decimal" value={value.weight ?? ""} onChange={set("weight")} className={fieldInputCls} />
      </Field>
      <Field label={t("hardness")}>
        <Input data-testid={`tyreset-${pos}-hardness-input`} type="number" step="0.1" inputMode="decimal" value={value.hardness ?? ""} onChange={set("hardness")} className={fieldInputCls} />
      </Field>
    </div>
  );
};

export const TyreSetDialog = ({ open, onOpenChange, eventId, tyreSet, onSaved }) => {
  const { t } = useT();
  const [form, setForm] = useState(blankSet);
  const targets = useSettings().settings.tyre_targets || [];
  const brands = [...new Set(targets.map((x) => x.brand))];
  const models = targets.filter((x) => !form.brand || x.brand.toLowerCase() === form.brand.trim().toLowerCase()).map((x) => x.model);
  useEffect(() => {
    if (open) {
      const b = blankSet();
      setForm(tyreSet ? { name: tyreSet.name, brand: tyreSet.brand || "", model: tyreSet.model || "", notes: tyreSet.notes || "", tyres: Object.fromEntries(TYRES.map((p) => [p, { ...b.tyres[p], ...tyreSet.tyres?.[p] }])) } : b);
    }
  }, [open, tyreSet]);

  const submit = async (e) => {
    e.preventDefault();
    const tyres = Object.fromEntries(TYRES.map((p) => [p, { ...form.tyres[p], circumference: num(form.tyres[p].circumference), weight: num(form.tyres[p].weight), hardness: num(form.tyres[p].hardness) }]));
    const payload = { name: form.name, brand: form.brand, model: form.model, notes: form.notes, tyres };
    try {
      const { data } = tyreSet ? await api.put(`/tyre-sets/${tyreSet.id}`, payload) : await api.post(`/events/${eventId}/tyre-sets`, payload);
      toast.success(t("saved"));
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tyreset-dialog" className="bg-[#12141C] border-[#282D3D] rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-2xl">{tyreSet ? t("edit_tyre_set") : t("new_tyre_set")}</DialogTitle>
          <DialogDescription className="sr-only">{t("tyre_sets")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label={`${t("set_name")} *`}>
            <Input data-testid="tyreset-name-input" required placeholder={t("set_name_hint")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldInputCls} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("tyre_brand")}>
              <Input data-testid="tyreset-brand-input" list="tyreset-brands" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={fieldInputCls} />
              <datalist id="tyreset-brands">{brands.map((b) => <option key={b} value={b} />)}</datalist>
            </Field>
            <Field label={t("tyre_model")}>
              <Input data-testid="tyreset-model-input" list="tyreset-models" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className={fieldInputCls} />
              <datalist id="tyreset-models">{models.map((m) => <option key={m} value={m} />)}</datalist>
            </Field>
          </div>
          {TYRES.map((p) => (
            <TyreRow key={p} pos={p} value={form.tyres[p]} onChange={(v) => setForm((f) => ({ ...f, tyres: { ...f.tyres, [p]: v } }))} />
          ))}
          <Field label={t("notes")}>
            <Textarea data-testid="tyreset-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${fieldInputCls} h-auto font-sans`} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" data-testid="tyreset-cancel-btn" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
            <Button type="submit" data-testid="tyreset-save-btn" className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-base px-6">{t("save")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
